import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {CellReference} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaASTNodes';
import {ICellIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/CellIOContext';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {SlotStatus} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotStatus';
import {
    SlotDependencies,
    SlotDependency,
    SlotDependencyType
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotGlobalKey, SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {OpenDesignState} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {SheetId, SlotId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Lambda, reaction} from 'mobx';

export class CellIOContextImpl implements ICellIOContext
{
    private readonly _slot:Slot;

    private readonly _id:SlotId;
    private readonly _sheetId:SheetId;
    private readonly _slotLocation:SlotLocation;

    private readonly _eventFrame:IFrame;

    constructor(slot:Slot, eventFrame?:IFrame)
    {
        this._slot = slot;

        this._id = slot.id;
        this._sheetId = slot.sheet.id;
        this._slotLocation = slot.location;

        this._eventFrame = eventFrame;
    }

    private _dependencies:Map<SlotGlobalKey, SlotDependency & {observerDisposer:Lambda}> = new Map();

    public dispose()
    {
        this._dependencies.forEach(({observerDisposer}) => observerDisposer());
        this._dependencies.clear();
    }

    public clone():ICellIOContext
    {
        return new CellIOContextImpl(this._slot);
    }

    public getDependencies():Map<SlotGlobalKey, SlotDependency>
    {
        return this._dependencies;
    }

    public addDependency(cellRef:CellReference, type:SlotDependencyType):boolean
    {
        // Allow functions to read self on a one-off basis
        if( cellRef.globalKey === this._slotLocation.toGlobalKey(this._sheetId) )
            return false;

        // do not track dependencies of a lazy slot.
        if( this.isSlotLazy() )
            return false;

        const {spreadsheets} = commonControllers;

        const dependency:SlotDependency = this._dependencies.get(cellRef.globalKey);

        if( dependency && dependency.type === type )
            return true;

        const workbook:Workbook = this._slot.workbook;
        const sheet:Sheet = spreadsheets.main.findAnySheetById(cellRef.sheetId) as Sheet;
        const slotLocation:SlotLocation = cellRef.toSlotLocation();

        let disposer:Lambda = null;

        if( !sheet )
        {
            disposer = reaction(() =>
            {
                return {
                    sheets: workbook.runtime.sheets as Sheet[],
                    keys: workbook.runtime.sheets.keys()
                };
            }, ({sheets}) =>
            {
                const targetSheet:Sheet = sheets.find(({id}) => id === cellRef.sheetId);

                if( targetSheet && targetSheet.slotLocationToSlot.has(slotLocation.toKey()) )
                    spreadsheets.sheet.reEvaluateSlot(this._slot.sheet.id, this._slot.location);
            });
        }
        else if( !sheet.getSlotByLocation(slotLocation) )
        {
            disposer = reaction(() =>
            {
                const sheet:Sheet = spreadsheets.main.findAnySheetById(cellRef.sheetId) as Sheet;

                return {
                    sheet: sheet,
                    slots: sheet ? sheet.slots : null,
                    keys: sheet ? sheet.slots.keys() : null
                };
            }, ({sheet}) =>
            {
                if( sheet && sheet.slotLocationToSlot.has(slotLocation.toKey()) )
                    spreadsheets.sheet.reEvaluateSlot(this._slot.sheet.id, this._slot.location);
            });
        }
        else
        {
            if( type === SlotDependencyType.Value )
                disposer = reaction(
                    () =>
                    {
                        const sheet:Sheet = spreadsheets.main.findAnySheetById(cellRef.sheetId) as Sheet;
                        const slot:Slot = sheet ? sheet.getSlotByLocation(slotLocation) : null;

                        return slot ? slot.value : null;
                    },
                    (value:IValue) => spreadsheets.sheet.reEvaluateSlot(
                        this._slot.sheet.id,
                        this._slot.location
                    )
                );
            else if( type === SlotDependencyType.Status )
                disposer = reaction(
                    () =>
                    {
                        const sheet:Sheet = spreadsheets.main.findAnySheetById(cellRef.sheetId) as Sheet;
                        const slot:Slot = sheet ? sheet.getSlotByLocation(slotLocation) : null;

                        return slot ? spreadsheets.sheet.getSlotStatus(slot.sheet.id, slot.location) : null;
                    },
                    (status:SlotStatus) => spreadsheets.sheet.reEvaluateSlot(
                        this._slot.sheet.id,
                        this._slot.location
                    )
                );
            else
            {
                disposer = reaction(
                    () =>
                    {
                        const sheet:Sheet = spreadsheets.main.findAnySheetById(cellRef.sheetId) as Sheet;
                        const slot:Slot = sheet ? sheet.getSlotByLocation(slotLocation) : null;

                        return {
                            value: slot ? slot.value : null,
                            status: slot ? spreadsheets.sheet.getSlotStatus(slot.sheet.id, slot.location) : null
                        };
                    },
                    ({value, status}) => (
                        spreadsheets.sheet.reEvaluateSlot(this._slot.sheet.id, this._slot.location)
                    ));
            }
        }

        this._dependencies.set(cellRef.globalKey, {
            type: dependency ? SlotDependencyType.All : type,
            observerDisposer: disposer
        });

        return true;
    }

    public addReadLocationHashDependency():void
    {
        const {designState, spreadsheets: {sheet}} = commonControllers;

        const openDesign:OpenDesignState = designState.getOpenDesign();

        this._dependencies.set(SlotDependencyType.LocationHash, {
            type: SlotDependencyType.LocationHash,
            observerDisposer: reaction(() => ({
                locationHash: openDesign.locationHash
            }), ({locationHash}) => (
                sheet.reEvaluateSlot(this._slot.sheet.id, this._slot.location)
            ))
        });
    }

    public addReadViewportInfoDependency():void
    {
        const visualDesigner = rootState.ui.visualDesigner;
        const {surfaceArea, spreadsheets: {sheet}} = commonControllers;

        const surfaceState:SurfaceState = surfaceArea.getState();

        this._dependencies.set(SlotDependencyType.ViewportInfo, {
            type: SlotDependencyType.ViewportInfo,
            observerDisposer: reaction(() => ({
                openDesign: visualDesigner.openDesign,
                designState: visualDesigner.openDesign ? visualDesigner.openDesign.designState : null,
                surface: visualDesigner.openDesign && visualDesigner.openDesign.designState
                    ? visualDesigner.openDesign.designState.surface
                    : null,
                viewportSize: surfaceState ? surfaceState.viewportSize : null
            }), ({viewportSize}) => (
                sheet.reEvaluateSlot(this._slot.sheet.id, this._slot.location)
            ))
        });
    }

    public getEventFrame():IFrame
    {
        return this._eventFrame;
    }

    public getSlotId():SlotId
    {
        return this._id;
    }

    public getSlotSheetId():SheetId
    {
        return this._sheetId;
    }

    public getSlotWorkbookId():WorkbookId
    {
        const {spreadsheets: {main}} = commonControllers;

        const sheet:Sheet = main.findAnySheetById(this._sheetId, true) as Sheet;

        return sheet ? sheet.workbook.id : null;
    }

    public getSlotLocation():SlotLocation
    {
        return this._slot ? this._slot.location : this._slotLocation;
    }

    public isSlotLazy():boolean
    {
        return this._slot ? this._slot.isLazy : false;
    }

    public isSlotLive():boolean
    {
        return this._slot ? this._slot.isLive : false;
    }

    public static detectCircularRef(slotGlobalKey:SlotGlobalKey, slotDependenciesMap:Map<SlotGlobalKey, SlotDependencies>):boolean
    {
        const visitedSlots:Set<SlotGlobalKey> = new Set<SlotGlobalKey>();

        let isCircularRefDetected:boolean = false;

        function visitSlot(visitSlotGlobalKey:SlotGlobalKey, markSlot:boolean):void
        {
            if( isCircularRefDetected )
                return;

            // found CircularRef
            if( visitedSlots.has(slotGlobalKey) )
            {
                isCircularRefDetected = true;
                return;
            }

            // skip visited slot
            if( visitedSlots.has(visitSlotGlobalKey) )
                return;

            // mark current slot as visited
            if( markSlot )
                visitedSlots.add(visitSlotGlobalKey);

            // visit all dependencies
            const slotDependencies:SlotDependencies = slotDependenciesMap.get(visitSlotGlobalKey);

            if( slotDependencies )
                slotDependencies.forEach((_, slotDependency) => visitSlot(slotDependency, true));
        }

        visitSlot(slotGlobalKey, false);

        return isCircularRefDetected;
    }
}
